import type { ReactNode } from 'react';
import { isApiClientError } from '../lib/api';
import type { ApiClientError } from '../lib/api';
import { Badge } from './ui/badge';

/**
 * Podpowiedz naprawcza dla konkretnego kodu bledu.
 *
 * `text` mowi, CO zrobic; `link` prowadzi tam, gdzie da sie to zrobic.
 */
export type ErrorHint = {
  readonly text: string;
  readonly link: { readonly href: string; readonly label: string } | null;
};

/**
 * `SettingsPage` trzyma aktywna zakladke we WLASNYM stanie, nie w adresie, wiec
 * gleboki link do konkretnej zakladki dzis nie istnieje — kierujemy do
 * `/settings` i nazywamy zakladke w etykiecie. Gdy zakladki trafia do search
 * paramow, tutaj wystarczy dopisac `?tab=byok`.
 */
const SETTINGS_BYOK: ErrorHint['link'] = { href: '/settings', label: 'Ustawienia › BYOK' };
const SETTINGS_BILLING: ErrorHint['link'] = { href: '/settings', label: 'Ustawienia › Rozliczenia' };

/**
 * Kod bledu → jedno zdanie, ktore zamienia komunikat serwera w akcje.
 *
 * Mapa jest CELOWO niepelna: kod spoza niej nie chowa bannera ani nie zmienia
 * jego tresci — uzytkownik dalej widzi surowy `message` i `requestId`, bo to
 * jedyne, co da sie wtedy przekazac dalej do supportu.
 */
const ERROR_HINTS: Readonly<Record<string, ErrorHint>> = {
  // Rodzina BYOK: `byok_key_required` rzuca `services/billing/byok.ts`, dwa
  // pozostale kody prowadza do dokladnie tej samej akcji uzytkownika.
  byok_key_required: {
    text: 'To konto rozlicza sie wlasnym kluczem dostawcy, a klucza nie ma. Zapisz go w ustawieniach.',
    link: SETTINGS_BYOK,
  },
  byok_key_missing: {
    text: 'Brakuje klucza dostawcy dla tego konta. Zapisz go w ustawieniach.',
    link: SETTINGS_BYOK,
  },
  byok_required: {
    text: 'Ta operacja wymaga wlasnego klucza dostawcy. Zapisz go w ustawieniach.',
    link: SETTINGS_BYOK,
  },
  platform_key_not_allowed: {
    text: 'Plan konta nie obejmuje klucza platformy. Zapisz wlasny klucz albo zmien plan.',
    link: SETTINGS_BYOK,
  },
  insufficient_credits: {
    text: 'Saldo kredytow nie pokrywa tego kroku. Doladuj konto albo przelacz je na wlasny klucz (BYOK).',
    link: SETTINGS_BILLING,
  },
  // Rzucane przez preflight generacji (services/generation/preflight.ts) dla
  // konta z ustawionym `suspended_at`. Uzytkownik nie odblokuje tego sam.
  account_suspended: {
    text: 'Konto jest zawieszone, wiec operacje wydajace pieniadze sa wstrzymane. Skontaktuj sie z obsluga konta.',
    link: null,
  },
  // `monthly_cap_exceeded` trzymamy razem z `budget_exceeded` (to ten drugi
  // rzuca dzis `services/budget.ts`) — dla uzytkownika oba znacza limit wydatkow.
  monthly_cap_exceeded: {
    text: 'Miesieczny limit wydatkow zostal wyczerpany. Podnies limit albo poczekaj na nowy okres rozliczeniowy.',
    link: SETTINGS_BILLING,
  },
  budget_exceeded: {
    text: 'Budzet dla tego okresu zostal wyczerpany. Podnies limit albo poczekaj na nowy okres rozliczeniowy.',
    link: SETTINGS_BILLING,
  },
  no_assist_model: {
    text: 'Asystent jest wylaczony w rejestrze modeli — podpowiedzi AI wroca, gdy operator wlaczy model. Scene mozna dalej skladac recznie.',
    link: null,
  },
};

const OWNER_ONLY_HINT: ErrorHint = {
  text: 'Ta operacja wymaga roli owner — popros ownera konta, zeby ja wykonal.',
  link: null,
};

/** Lista rol z `details.required` (`middleware/tenant.ts`, `routes/billing.ts`). */
function requiredRoles(details: unknown): readonly string[] {
  if (typeof details !== 'object' || details === null) return [];
  const value = (details as { readonly required?: unknown }).required;
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/** Podpowiedz dla bledu API albo `null`, gdy kod nie ma znanej akcji naprawczej. */
export function errorHint(error: unknown): ErrorHint | null {
  if (!isApiClientError(error)) return null;
  const api: ApiClientError = error;
  // 403 `forbidden` jest generyczny — dopiero `details.required` mowi, czego brakuje.
  if (api.code === 'forbidden') {
    return requiredRoles(api.details).includes('owner') ? OWNER_ONLY_HINT : null;
  }
  return ERROR_HINTS[api.code] ?? null;
}

/** Jednolity komunikat bledu z kodem i requestId — dokladnie to, co API zwraca. */
export function ErrorBanner({ error, className }: { readonly error: unknown; readonly className?: string }) {
  if (error === null || error === undefined) return null;
  const api = isApiClientError(error) ? error : null;
  const hint = errorHint(error);
  return (
    <div
      role="alert"
      className={`rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200 ${className ?? ''}`}
    >
      <p className="font-medium">
        {api === null ? 'Blad' : `${String(api.status)} ${api.code}`}
      </p>
      <p className="mt-0.5">{api === null ? String(error) : api.message}</p>
      {hint === null ? null : (
        <p className="mt-2 text-[13px]" data-testid="error-hint">
          {hint.text}
          {hint.link === null ? null : (
            <>
              {' '}
              <a
                href={hint.link.href}
                className="font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              >
                {hint.link.label}
              </a>
            </>
          )}
        </p>
      )}
      {api?.requestId === null || api?.requestId === undefined ? null : (
        <p className="mt-1 font-mono text-[11px] opacity-70">requestId: {api.requestId}</p>
      )}
    </div>
  );
}

export function Loading({ label = 'Wczytuje…' }: { readonly label?: string }) {
  return (
    <p role="status" className="text-sm text-neutral-500 dark:text-neutral-400">
      {label}
    </p>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  readonly title: string;
  readonly hint?: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-dashed border-neutral-300 p-8 text-center dark:border-neutral-700">
      <p className="text-sm font-medium">{title}</p>
      {hint === undefined ? null : (
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>
      )}
      {action === undefined ? null : <div className="mt-3 flex justify-center">{action}</div>}
    </div>
  );
}

/** Informacja „ten endpoint przyjdzie w pozniejszym milestone" (HTTP 501). */
export function NotImplementedNotice({ what, error }: { readonly what: string; readonly error: unknown }) {
  const api = isApiClientError(error) ? error : null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      <div className="flex items-center gap-2">
        <Badge tone="warning">501</Badge>
        <span className="font-medium">{what}</span>
      </div>
      <p className="mt-1 text-xs">
        {api === null ? 'Endpoint nie jest jeszcze zaimplementowany.' : api.message}
      </p>
    </div>
  );
}
